import { first } from 'rxjs/operators';
import { NotificationService } from './notification.service';
import { ClinicStatus } from './../_enums/clinicStatus.enum';
import { Injectable } from '@angular/core';
import {
  Action,
  AngularFirestore,
  DocumentChangeAction,
  DocumentReference,
  DocumentSnapshot,
  QuerySnapshot,
} from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import * as moment from 'moment';
import { NotificationType } from '../_enums/notificationType.enum';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class FirestoreService {
  constructor(
    private db: AngularFirestore,
    private _notificationService: NotificationService,
    private _httpClient: HttpClient
  ) {}

  // Clinics

  addClinic(data): Promise<DocumentReference<unknown>> {
    return this.db.collection('clinics').add(data);
  }
  updateClinic(id, data): Promise<void> {
    return this.db.doc('clinics/' + id).update(data);
  }
  deleteClinic(id): Promise<void> {
    return this.db.doc('clinics/' + id).delete();
  }
  getClinics(): Observable<DocumentChangeAction<unknown>[]> {
    return this.db.collection('clinics').snapshotChanges();
  }
  getClinicsV1(): Observable<any> {
    return this.db.collection('clinics').valueChanges();
  }
  getClinicWithId(id: string): Observable<Action<DocumentSnapshot<unknown>>> {
    return this.db.collection('clinics').doc(id).snapshotChanges();
  }

  // staffs

  addStaff(data): Promise<DocumentReference<unknown>> {
    return this.db.collection('staffs').add(data);
  }
  updateStaff(id, data): Promise<void> {
    return this.db.doc('staffs/' + id).update(data);
  }
  getStaffs(clinic_id: string): Observable<DocumentChangeAction<unknown>[]> {
    const clinicRef = this.db.collection('clinics').doc(clinic_id).ref;
    return this.db
      .collection('staffs', (ref) => ref.where('clinic_id', '==', clinicRef))
      .snapshotChanges();
  }
  deleteStaff(id): Promise<void> {
    return this.db.doc('staffs/' + id).delete();
  }
  // Announcements

  async addAnnouncements(data,announcementType: string): Promise<any> {
    return this._notificationService
      .sendNotification({
        type: announcementType,
        referenceId: await (
          await this.db.collection('announcements').add(data)
        ).id,
      })
      .pipe(first())
      .toPromise();
  }

  async updateAnnouncement(id, data,announcementType: string): Promise<any> {
    await this.db.doc('announcements/' + id).update(data);
    return this._notificationService
      .sendNotification({
        type: announcementType,
        referenceId: id,
      })
      .pipe(first())
      .toPromise();
  }
  
  getAnnouncements(): Observable<DocumentChangeAction<unknown>[]> {
    return this.db.collection('announcements').snapshotChanges();
  }
  getAnnouncementWithId(
    id: string
  ): Observable<Action<DocumentSnapshot<unknown>>> {
    return this.db.collection('announcements').doc(id).snapshotChanges();
  }
  deleteAnnouncement(id: string): Promise<void> {
    return this.db.doc('announcements/' + id).delete();
  }

  // Patients

  addPatient(data): Promise<DocumentReference<unknown>> {
    data.status = ClinicStatus.ACTIVE;
    return this.addPatientInAuth(data).pipe(first()).toPromise();
  }

  getPatients(): Observable<DocumentChangeAction<unknown>[]> {
    return this.db.collection('patients').snapshotChanges();
  }
  getPatientsWithId(id: string): Observable<Action<DocumentSnapshot<unknown>>> {
    return this.db.collection('patients').doc(id).snapshotChanges();
  }
  updatePatient(id, data): Promise<any> {
    return this.updatePatientInAuth(data, id).pipe(first()).toPromise();
  }

  deletePatient(id): Promise<void> {
    return this.db.doc('patients/' + id).delete();
  }
  addPatientInAuth(payload: any): Observable<any> {
    payload = {
      ...payload,
      languages: payload.languages.split(','),
      dental_needs: payload.dental_needs.split(','),
      fromClinic: true,
      dob: moment(payload.dob).valueOf(),
    };
    return this._httpClient.post<any>(
      `${environment.firebase.cloudFunctionUrl}/app/api/patients/patient`,
      payload
    );
  }
  updatePatientInAuth(payload: any, id: string): Observable<any> {
    payload = {
      ...payload,
      languages: payload.languages,
      fromClinic: true,
      dob: moment(payload.dob).valueOf(),
    };
    console.log(payload);
    return this._httpClient.put<any>(
      `${environment.firebase.cloudFunctionUrl}/app/api/patients/patient/${id}`,
      payload
    );
  }
  // Appointments

  async addAppointment(data): Promise<any> {
    return this._notificationService
      .sendNotification({
        type: NotificationType.APPOINTMENT,
        referenceId: await (
          await this.db.collection('appointments').add(data)
        ).id,
      })
      .pipe(first())
      .toPromise();
  }
  async updateAppointment(id, data): Promise<any> {
    await this.db.doc('appointments/' + id).update(data);
    return this._notificationService
      .sendNotification({
        type: NotificationType.APPOINTMENT,
        referenceId: id,
      })
      .pipe(first())
      .toPromise();
  }
  getAppointments(): Observable<DocumentChangeAction<unknown>[]> {
    return this.db
      .collection('appointments', (ref) =>
        ref.orderBy('confirmed_date', 'desc')
      )
      .snapshotChanges();
  }
  getAlongWithPendingAppointments(): Observable<
    DocumentChangeAction<unknown>[]
  > {
    return this.db
      .collection('appointments', (ref) => ref.orderBy('createdAt', 'desc'))
      .snapshotChanges();
  }
  getAppointmentsByDate(
    startDate: Date,
    endDate: Date
  ): Observable<DocumentChangeAction<unknown>[]> {
    return this.db
      .collection('appointments', (ref) =>
        ref
          .where('confirmed_date', '>=', startDate)
          .where('confirmed_date', '<=', endDate)
      )
      .snapshotChanges();
  }
  getTodayAppointments(): Observable<DocumentChangeAction<unknown>[]> {
    return this.db
      .collection('appointments', (ref) =>
        ref
          .where('confirmed_date', '>=', moment().startOf('day').toDate())
          .where('confirmed_date', '<=', moment().endOf('day').toDate())
      )
      .snapshotChanges();
  }
  getTodayRequests(): Observable<DocumentChangeAction<unknown>[]> {
    return this.db
      .collection('appointments', (ref) =>
        ref
          .where('createdAt', '>=', moment().startOf('day').toDate())
          .where('createdAt', '<=', moment().endOf('day').toDate())
      )
      .snapshotChanges();
  }
  getAppointmentsWithId(
    id: string
  ): Observable<Action<DocumentSnapshot<unknown>>> {
    return this.db.collection('appointments').doc(id).snapshotChanges();
  }
  deleteAppointment(id: string): Promise<void> {
    return this.db.doc('appointments/' + id).delete();
  }

  // notifications
  getNotifications() {
    return this.db
      .collection('notifications')
      .doc('ClinicAll')
      .snapshotChanges();
  }
  getNotificationWithId(
    id: string
  ): Observable<Action<DocumentSnapshot<unknown>>> {
    return this.db.collection('notifications').doc(id).snapshotChanges();
  }
  addNotification(data): Promise<DocumentReference<unknown>> {
    return this.db.collection('notifications').add(data);
  }
  updateNotification(id, data): Promise<void> {
    return this.db.doc('notifications/' + id).update(data);
  }
  deleteNotification(id): Promise<void> {
    return this.db.doc('notifications/' + id).delete();
  }

  // Requests
  addRequest(data): Promise<DocumentReference<unknown>> {
    return this.db.collection('requests').add(data);
  }
  getRequests(): Observable<DocumentChangeAction<unknown>[]> {
    return this.db.collection('requests').snapshotChanges();
  }
  getRequestsWithId(id: string): Observable<Action<DocumentSnapshot<unknown>>> {
    return this.db.collection('requests').doc(id).snapshotChanges();
  }
  updateRequest(id, data): Promise<void> {
    return this.db.doc('requests/' + id).update(data);
  }
  deleteRequest(id): Promise<void> {
    return this.db.doc('requests/' + id).delete();
  }
  // ---------------------
  getPatientAppointments(id: string): Promise<QuerySnapshot<unknown>> {
    const patientRef = this.db.collection('patients').doc(id).ref;
    return this.db
      .collection('appointments')
      .ref.where('patient_id', '==', patientRef)
      .get();
  }
  getPatientbyClinicId(id: string): Promise<QuerySnapshot<unknown>> {
    const clinicRef = this.db.collection('clinics').doc(id).ref;
    return this.db
      .collection('patients')
      .ref.where('clinic_id', '==', clinicRef)
      .get();
  }

  updateAdmin(id, data): Promise<void> {
    return this.db.doc('admins/' + id).update(data);
  }
  getAdminWithQuery(query): Promise<QuerySnapshot<unknown>> {
    return this.db
      .collection('admins')
      .ref.where(query[0], query[1], query[2])
      .get();
  }
  getClinicWithQuery(query): Promise<QuerySnapshot<unknown>> {
    return this.db
      .collection('clinics')
      .ref.where(query[0], query[1], query[2])
      .get();
  }
  getStaffWithQuery(query): Promise<QuerySnapshot<unknown>> {
    return this.db
      .collection('users')
      .ref.where(query[0], query[1], query[2])
      .get();
  }
}
